import { isAfter, subDays } from 'date-fns';
import { parseDateRange } from '../../../helpers/dateFormattingHelpers';
import {
  dashboardLookmlJsonToYaml,
  dashboardLookmlYamlToJson,
  resolveDashboardLookmlJson,
} from '../../../hooks/useLooker';
import { TIMEFRAME_FIELD } from '../../../modules/BrandExplorerWorkspaceView/helpers/constants';
import { getLookerConfig, mapEnvironmentToBackendFormat } from '../../lookerConfig';
import { api } from '../baseApi';
import { makeRequest } from '../makeRequest';
import { WorkspaceAPIRoutes } from '../workspaces';
import type {
  AcquireCookielessSessionRequest,
  AcquireCookielessSessionResponse,
  AlterEmbedAccessRequest,
  AlterEmbedAccessResponse,
  DashboardFilter,
  DashboardFiltersResponse,
  ExecuteLookerQueryRequest,
  ExecuteLookerQueryResponse,
  GenerateCookielessTokensRequest,
  GenerateCookielessTokensResponse,
  GetDashboardLookMLRequest,
  GetDashboardLookMLResponse,
  GetLookerSuggestionsRequest,
  GetLookerSuggestionsResponse,
  ImportDashboardFromLookMLRequest,
  ImportDashboardLookMLBackendRequest,
  ImportDashboardLookMLBackendResponse,
  LookerUser,
} from './dto';
import { LookerAPIRoutes } from './route';

export function alterEmbedAccess(params: AlterEmbedAccessRequest): Promise<AlterEmbedAccessResponse> {
  return makeRequest(api.put(LookerAPIRoutes.alterEmbedAccess(), params));
}

export async function getDashboardLookML(body: GetDashboardLookMLRequest): Promise<string> {
  const res = await makeRequest(
    api.post<GetDashboardLookMLResponse>(WorkspaceAPIRoutes.getDashLookML(), {
      objectList: body.objectList,
      source: body.source ?? 'looker',
      version: 'v2',
    }),
  );

  const content = typeof res?.fileContents === 'string' ? [res?.fileContents] : res?.fileContents;

  if (!content || content.length === 0) {
    throw new Error('Could not find LookML response');
  }

  const jsonDefinitions = content.map(dashboardLookmlYamlToJson);
  const combinedJsonDefinition = resolveDashboardLookmlJson(jsonDefinitions);

  if (!combinedJsonDefinition || combinedJsonDefinition.length === 0) {
    throw new Error('Could not parse LookML response');
  }

  if (body.slug) {
    combinedJsonDefinition[0].preferred_slug = body.slug;
  }

  combinedJsonDefinition[0].title = 'Studio';

  const yamlDefinition = dashboardLookmlJsonToYaml(combinedJsonDefinition);

  if (!yamlDefinition) {
    throw new Error('Could not parse LookML response');
  }

  return yamlDefinition;
}

export const importDashboardFromLookML = async (body: ImportDashboardFromLookMLRequest): Promise<string> => {
  const { lookml, folderId } = body;

  if (!lookml) {
    throw new Error('Could not parse LookML response');
  }

  const response = await importDashboardFromLookMLViaBackend({ lookml, folderId });

  // Accept { dashboardId }, { dashboard_id }, or { response: { dashboardId | dashboard_id }, eventID }.
  const dashboardId =
    response?.dashboardId ??
    response?.dashboard_id ??
    response?.response?.dashboardId ??
    response?.response?.dashboard_id;

  if (!dashboardId) {
    throw new Error('Could not load dashboard.');
  }

  return dashboardId;
};

export const executeLookerQuery = async <T>(
  { result_format, ...body }: ExecuteLookerQueryRequest,
  signal?: AbortSignal,
) => {
  const timeframeFilter = body.filters?.[TIMEFRAME_FIELD];
  if (timeframeFilter) {
    const range = parseDateRange(timeframeFilter);
    if (isAfter(range.from, subDays(range.to, 1))) throw new Error('Start date cannot be later than end date.');
  }

  const response: ExecuteLookerQueryResponse<T> = await makeRequest(
    api.post(
      LookerAPIRoutes.executeQuery(),
      { ...body, referrerURL: undefined },
      { params: { result_format }, signal },
    ),
  );

  const query_result = result_format === 'json' ? JSON.parse(response.query_result as string) : response.query_result;
  return { ...response, query_result } as ExecuteLookerQueryResponse<T>;
};

export const getLookerSuggestions = async (params: GetLookerSuggestionsRequest) => {
  return api.get<GetLookerSuggestionsResponse>(LookerAPIRoutes.getSuggestions(), { params });
};

// ---------------------------------------------------------------------------
// Backend-proxy endpoints (/v7/looker/*). All Looker traffic goes through
// bi-api — the browser never talks to Looker directly.
// ---------------------------------------------------------------------------

/** `GET /v7/looker/user/me` — replaces `sdk.me()`. */
export function fetchLookerUserInfo(): Promise<LookerUser> {
  return makeRequest(api.get<LookerUser>(LookerAPIRoutes.me()));
}

/**
 * `GET /v7/looker/user/attributes?names=…` — replaces
 * `sdk.user_attribute_user_values`. Returns a name→value map; each attribute
 * may be `null` if unset for the current user.
 */
export function fetchLookerUserAttributes(names: string[]): Promise<Record<string, string | null>> {
  return makeRequest(
    api.get<Record<string, string | null>>(LookerAPIRoutes.userAttributes(), {
      params: { names: names.join(',') },
    }),
  );
}

/**
 * `GET /v7/looker/dashboards/{slug}/filters` — replaces
 * `sdk.dashboard_dashboard_filters`. Backend resolves the LookML slug and
 * returns the standard `{ response, eventID }` bi-api envelope.
 */
export async function fetchDashboardFilters(slug: string): Promise<DashboardFilter[]> {
  const envelope = await makeRequest(api.get<DashboardFiltersResponse>(LookerAPIRoutes.dashboardFilters(slug)));
  return Array.isArray(envelope?.response) ? envelope.response : [];
}

/**
 * `POST /v7/looker/dashboards/import_lookml` — backend imports a dashboard
 * from raw LookML. Backend owns the retry loop and calls Looker's internal
 * `/api/internal/core/4.0/dashboards/lookml` endpoint.
 */
export function importDashboardFromLookMLViaBackend(
  body: ImportDashboardLookMLBackendRequest,
): Promise<ImportDashboardLookMLBackendResponse> {
  return makeRequest(api.post<ImportDashboardLookMLBackendResponse>(LookerAPIRoutes.importDashboardLookML(), body));
}

/**
 * `POST /v7/looker/embed/acquire_session` — bootstraps a cookieless embed
 * session. Backend derives `embed_domain`, `models`, `permissions`,
 * `user_attributes`, and `session_length` from the authenticated user.
 */
export function acquireCookielessSession(
  params: AcquireCookielessSessionRequest = {},
): Promise<AcquireCookielessSessionResponse> {
  return makeRequest(
    api.post<AcquireCookielessSessionResponse>(LookerAPIRoutes.acquireEmbedSession(), mapAcquireSessionBody(params)),
  );
}

// bi-api expects the body wrapped as `{ env, requestParams: { …snake_case } }`
// (legacy /v4/cookieless/acquire_session shape). Two declarative maps convert
// our internal camelCase fields to the wire shape:
//   REQUEST_PARAM_KEYS → top-level requestParams keys
//   USER_ATTRIBUTE_KEYS → keys nested under requestParams.user_attributes
// `lookerUserId` intentionally appears in both — it becomes
// `external_user_id` at the top level *and* `user_attributes.userid`.
const REQUEST_PARAM_KEYS: Partial<Record<keyof AcquireCookielessSessionRequest, string>> = {
  embedDomain: 'embed_domain',
  lookerUserId: 'external_user_id',
  firstName: 'first_name',
  lastName: 'last_name',
  email: 'email',
  forceLogoutLogin: 'force_logout_login',
  models: 'models',
  permissions: 'permissions',
  sessionLength: 'session_length',
};

const USER_ATTRIBUTE_KEYS: Partial<Record<keyof AcquireCookielessSessionRequest, string>> = {
  accountId: 'accountid',
  lookerUserId: 'userid',
  platformSessionId: 'platform_session_id',
  advertiserId: 'advertiserid',
  workspaceTypeId: 'workspaceTypeId',
};

function projectFields(
  p: AcquireCookielessSessionRequest,
  map: Partial<Record<keyof AcquireCookielessSessionRequest, string>>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(map)
      .map(([feKey, wireKey]) => [wireKey, p[feKey as keyof AcquireCookielessSessionRequest]] as const)
      .filter(([wireKey, value]) => wireKey !== undefined && value !== undefined),
  );
}

function mapAcquireSessionBody(p: AcquireCookielessSessionRequest): {
  env: 'development' | 'staging' | 'production';
  requestParams: Record<string, unknown>;
} {
  const topLevel = projectFields(p, REQUEST_PARAM_KEYS);
  const userAttributes = { ...p.userAttributes, ...projectFields(p, USER_ATTRIBUTE_KEYS) };
  const requestParams =
    Object.keys(userAttributes).length > 0 ? { ...topLevel, user_attributes: userAttributes } : topLevel;

  return {
    env: mapEnvironmentToBackendFormat(getLookerConfig().environment),
    requestParams,
  };
}

/** `POST /v7/looker/embed/generate_tokens` — refresh cookieless session tokens. */
export function generateCookielessTokens(
  params: GenerateCookielessTokensRequest = {},
): Promise<GenerateCookielessTokensResponse> {
  return makeRequest(api.post<GenerateCookielessTokensResponse>(LookerAPIRoutes.generateEmbedTokens(), params));
}
